import { doc, setDoc } from 'firebase/firestore'
import { firestore, iamAuth } from '@/lib/firebase'
import type { Transaction } from '@/features/transactions/types'
import { fetchSchedules, saveSchedule } from './repository'
import type { Frequency, ScheduledTransaction } from './types'

/**
 * Mirrors `Budget/Services/ScheduledTransactionRunner.swift`: for each
 * enabled schedule with nextOccurrence <= now, create the real
 * Transaction and advance nextOccurrence. Catch-up safe — three missed
 * monthly occurrences create three transactions.
 *
 * Divergence from iOS (intentional; iOS bug for the parity ledger):
 * iOS saves the fired amount as abs(amount) even for expenses. Web
 * signs it (negative for expense) to match the Transaction contract.
 */

export function advanceOccurrence(iso: string, frequency: Frequency): string {
  const d = new Date(iso)
  switch (frequency) {
    case 'weekly':
      d.setDate(d.getDate() + 7)
      break
    case 'biweekly':
      d.setDate(d.getDate() + 14)
      break
    case 'monthly':
      d.setMonth(d.getMonth() + 1)
      break
    case 'yearly':
      d.setFullYear(d.getFullYear() + 1)
      break
  }
  return d.toISOString()
}

function firedTransaction(s: ScheduledTransaction, occurrenceISO: string): Transaction {
  const magnitude = Math.abs(s.amount)
  const amount = s.transactionType === 'income' ? magnitude : -magnitude
  return {
    id: crypto.randomUUID(),
    date: occurrenceISO,
    payeeId: null,
    payeeName: s.payeeName ?? null,
    categoryId: s.categoryId ?? null,
    accountId: s.accountId,
    amount,
    memo: s.memo ?? null,
    cleared: 'uncleared',
    isInflow: amount > 0,
    splits: [],
    isTransfer: false,
    transferAccountId: null,
    transferId: null,
    isCreditCardPayment: false,
    originalCategory: null,
    description: null,
  }
}

// Bail-out for pathological docs (e.g. a startDate years in the past
// with a weekly cadence) so a bad doc can't spin hundreds of writes.
const MAX_FIRES_PER_SCHEDULE = 60

export async function runScheduledCatchUp(now = new Date()): Promise<number> {
  const uid = iamAuth.currentUser?.uid
  if (!uid) return 0
  const schedules = await fetchSchedules()
  let created = 0

  for (const s of schedules) {
    if (!s.isEnabled) continue
    let next = s.nextOccurrence
    let fires = 0
    while (new Date(next) <= now && fires < MAX_FIRES_PER_SCHEDULE) {
      if (s.endDate && new Date(next) > new Date(s.endDate)) break
      const tx = firedTransaction(s, next)
      await setDoc(doc(firestore, 'users', uid, 'transactions', tx.id), tx)
      created += 1
      fires += 1
      next = advanceOccurrence(next, s.frequency)
    }
    if (next !== s.nextOccurrence) {
      await saveSchedule({
        ...s,
        nextOccurrence: next,
        lastCreatedDate: now.toISOString(),
        updatedAt: now.toISOString(),
      })
    }
  }
  return created
}
